import {
  Api,
  FullRequestParams,
  HttpClient,
  HttpResponse,
} from '@asset-metrix/portal-gateway-client-js';
import { AsyncNotification } from '@asset-metrix/portal-ui';

import { PORTAL_GATEWAY_API_BASE_URL } from '../config';
import { useStore } from '../store';
import { HTTP_STATUS } from '../utils/constants';
import { getTenantNameBySubdomain } from '../utils/getTenantNameBySubdomain';
import {
  MAINTENANCE_NOTIFICATION_ID,
  showMaintenanceNotification,
} from '../utils/showMaintenanceNotification';

import { HttpError } from './errors';
import { getPortalAcceptLanguage } from './portalAcceptLanguage';
import { getRequestId } from './tracing';

export const NO_ROLE_ID = 'no-role-id';

export const getPublicApiHeaders = () => {
  const {
    activeUserRole,
    sessionId,
    tenantName: tenantNameFromStore,
  } = useStore.getState();
  const roleId = activeUserRole?.id;

  let tenantName = tenantNameFromStore;
  if (tenantName == null) {
    try {
      tenantName = getTenantNameBySubdomain();
    } catch {
      tenantName = undefined;
    }
  }

  return {
    ...(tenantName != null && { 'X-Tenant-Name': tenantName }),
    ...(roleId != null && { 'X-Role-ID': roleId }),
    'Accept-Language': getPortalAcceptLanguage(),
    'X-Request-ID': getRequestId(),
    'X-Session-ID': sessionId,
  };
};

export function getRequestOptions() {
  const { session } = useStore.getState();
  const authToken = session?.getAuthToken();

  return {
    headers: {
      ...getPublicApiHeaders(),
      authorization: `Bearer ${authToken}`,
    },
  };
}

function mergeFetchHeaders(init?: RequestInit): HeadersInit {
  const initHeaders =
    init?.headers == null
      ? {}
      : init.headers instanceof Headers
        ? Object.fromEntries(init.headers.entries())
        : Array.isArray(init.headers)
          ? Object.fromEntries(init.headers)
          : { ...init.headers };

  return {
    ...initHeaders,
    ...getRequestOptions().headers,
  };
}

export async function fetchWithSessionRefresh(
  url: string | URL,
  init?: RequestInit
): Promise<Response> {
  const requestUrl = url.toString();
  const request = () =>
    fetch(requestUrl, {
      ...init,
      headers: mergeFetchHeaders(init),
    });

  const response = await request();
  if (response.status !== HTTP_STATUS.UNAUTHORIZED) {
    return response;
  }

  const { refreshSession, session } = useStore.getState();
  if (session == null) {
    return response;
  }

  try {
    await refreshSession();
  } catch {
    return response;
  }

  return request();
}

export function createApi(customFetch?: typeof fetch) {
  const httpClient = new HttpClient({
    baseUrl: PORTAL_GATEWAY_API_BASE_URL.replace(/\/$/, ''),
    securityWorker: () => ({
      headers: getRequestOptions().headers,
    }),
    ...(customFetch && { customFetch }),
  });

  console.debug(
    'Portal gateway client initialized with baseUrl %s',
    PORTAL_GATEWAY_API_BASE_URL.replace(/\/$/, '')
  );

  // Patch contentFormatters to support file arrays for form data, which is currently
  // not supported by swagger-typescript-api
  // c.f. https://github.com/acacode/swagger-typescript-api/issues/776
  const originalFormDataFormatter =
    // @ts-expect-error Patch internal contentFormatters to support arrays in form-data
    httpClient.contentFormatters['multipart/form-data'];

  const patchedFormDataFormatter = (input: Record<string, unknown>) => {
    const formData = originalFormDataFormatter(input) as FormData;
    for (const key of Object.keys(input)) {
      const value = input[key];
      if (Array.isArray(value)) {
        formData.delete(key);
        for (const entry of value) {
          if (!(entry instanceof Blob) && typeof entry !== 'string') {
            throw new TypeError(
              'Only string or Blob values are supported to be used in array for form-data requests'
            );
          }
          formData.append(key, entry);
        }
      }
    }
    return formData;
  };

  // @ts-expect-error Patch internal contentFormatters to support arrays in form-data
  httpClient.contentFormatters['multipart/form-data'] =
    patchedFormDataFormatter;

  // Monkey patch request method to include token refresh and maintenance notification
  const originalRequestMethod = httpClient.request;
  httpClient.request = async function modifiedRequestMethod<
    T = unknown,
    E = unknown,
  >(params: FullRequestParams): Promise<HttpResponse<T, E>> {
    const res = await originalRequestMethod<T, E>(params);
    // Check for service unavailable (503) - show async notification only if user is logged in
    // and the maintenance notification is not already shown (avoid duplicates)
    // and the severity is not low
    if (res.status === HTTP_STATUS.SERVICE_UNAVAILABLE) {
      const { session } = useStore.getState();
      if (
        session?.getDecodedAuthToken() != null &&
        AsyncNotification.getNotification(MAINTENANCE_NOTIFICATION_ID) ==
          null &&
        (res.error as any)?.data?.severity !== 'low'
      ) {
        showMaintenanceNotification(res);
      }
    }

    // An http status code "unauthorized" could be caused by an expired access token. The refresh token might still be
    // valid, so we try a token refresh first
    if (res.status === HTTP_STATUS.UNAUTHORIZED) {
      const { refreshSession, session } = useStore.getState();

      if (session != null) {
        try {
          await refreshSession();
          return await originalRequestMethod<T, E>(params);
        } catch {
          throw new HttpError((await res.text()) ?? res.statusText, res);
        }
      }
    }

    return res;
  };
  return new Api(httpClient);
}

export const portalGatewayClient = createApi();
